import React, { useMemo } from 'react';
import { Alert, PageHeader } from '@/shared/components';
import { usePermissions } from '@/features/auth';
import { useQuote } from '../../../context/QuoteContext';
import { useQuoteBasketPricing } from '../../../hooks/useBasketPricing';
import { isLiveQuote } from '../../../utils/quoteStatus';
import { EVEN_MONTHLY_FAMILY_KEYS } from '../../../services/schedulePreview';
import { toBasketGross } from '../../../services/basketGross';
import { earliestPricedDueDate, isPastDueForScheduling } from '../../../services/pastDueScheduling';
import {
    collectionCountChoice,
    resolveChosenInstalments,
    transmittedCollectionCount,
} from '../../../services/collectionCount';
import { CollectionCountSelect } from '../../CollectionCountSelect';
import { mergePricedLines } from '../../../utils/pricedLines';
import { SchedulingMethodSelector } from '../../SchedulingMethodSelector';
import { CollectionLeadInput } from '../../CollectionLeadInput';
import { getProductCurrency } from '@/features/products/services/productCurrency';
import { termWarningCopy, resolveLeadRejectionMessage } from './copy';
import { resolveLeadRejection } from './derive';
import { resolveSelectionConfirmation } from './selectionConfirmation';
import { useScheduleDerivation } from './useScheduleDerivation';
import { usePaymentSelectionResume } from './usePaymentSelectionResume';
import { useDepositDerivation } from './useDepositDerivation';
import { useDepositCeiling, useProductDeposit } from './usePaymentDeposit';
import { useSchedulePreviewParams } from './useSchedulePreviewParams';
import { resolveControlsVisibility } from './controlsVisibility';
import { usePaymentSelectionCommit } from './usePaymentSelectionCommit';
import { useScheduleDepositWriteBack } from './useScheduleDepositWriteBack';
import { ReadOnlyPaymentView } from './ReadOnlyPaymentView';
import { DepositSection } from './DepositSection';
import { decimalToNumber } from '../../../utils/decimalMoney';
import { SchedulePreviewPane, SCHEDULE_PREVIEW_PERMISSION } from './SchedulePreviewPane';
import {
    TermWarningNotice,
    LeadRejectionNotice,
    UnpricedNotice,
    PricingPendingNotice,
    PricingErrorNotice,
    PastServiceDateNotice,
} from './PaymentNotices';

export const PaymentStep: React.FC = () => {
    const {
        quoteId,
        selectedProduct,
        status,
        paymentSelection,
        updatePaymentSelection,
        reportPaymentPreviewBlocked,
        configuredServices,
        configuredAddOns,
    } = useQuote();
    const { hasPermission } = usePermissions();

    const isReadOnly = !isLiveQuote(status);
    // The read-only view renders the confirmed selection, never a live price, so pricing the
    // basket against today's catalogue would be a request whose result is discarded.
    const pricing = useQuoteBasketPricing(!isReadOnly);

    const productDeposit = useProductDeposit(selectedProduct);

    const {
        selectedMethodKey,
        setSelectedMethodKey,
        depositMinor,
        depositOrigin,
        depositDiscards,
        onDepositInput,
        onDepositEdit,
        writeScheduleDeposit,
        discardProductDeposit,
        leadOverrideMonths,
        setLeadOverrideMonths,
        collectionInstalments,
        setCollectionInstalments,
    } = usePaymentSelectionResume(
        quoteId,
        paymentSelection,
        selectedProduct,
        configuredServices,
        configuredAddOns,
        productDeposit,
    );

    const methods = selectedProduct?.schedulingMethods ?? [];
    const selectedMethod = methods.find((method) => method.key === selectedMethodKey) ?? null;

    const {
        isDepositBearing,
        isItemBasedFamily,
        isItemBasedDepositBearing,
        depositMode,
        effectiveDepositMinor,
    } = useDepositDerivation(selectedMethod, selectedProduct, depositMinor);

    const isEvenMonthlyFamily = EVEN_MONTHLY_FAMILY_KEYS.has(selectedMethod?.key ?? '');
    const leadMonths = selectedProduct?.collectionLeadMonths ?? 0;
    const leadBounds = selectedProduct?.collectionLeadBounds ?? null;
    const effectiveLeadMonths =
        (leadBounds !== null ? leadOverrideMonths : undefined) ?? selectedProduct?.collectionLeadMonths ?? 0;

    const leadRejection = resolveLeadRejection(leadOverrideMonths, leadBounds, isItemBasedFamily);
    const leadOutOfBounds = leadRejection !== null;

    const minInstalments = selectedProduct?.minCollectionInstalments;
    const defaultInstalments = selectedProduct?.defaultCollectionInstalments;

    const recordedLeadMonths = isItemBasedFamily ? effectiveLeadMonths : undefined;

    const canPreviewSchedule = hasPermission(SCHEDULE_PREVIEW_PERMISSION);
    const currency = getProductCurrency(selectedProduct);

    const merged = useMemo(
        () => mergePricedLines(configuredServices, configuredAddOns, pricing.data),
        [configuredServices, configuredAddOns, pricing.data],
    );

    const basketGross = useMemo(() => toBasketGross(pricing, merged), [pricing, merged]);

    const basketGrossMinor = basketGross.kind === 'derived' ? basketGross.amountMinor : 0;

    const {
        itemBased,
        evenMonthly,
        anchor,
        unavailableReasons,
    } = useScheduleDerivation({
        isItemBasedFamily,
        leadMonths,
        effectiveLeadMonths,
        minInstalments,
        defaultInstalments,
        selectedMethodKey,
        depositMinor: effectiveDepositMinor,
        configuredServices: merged.services,
        configuredAddOns: merged.addOns,
        feeGross: pricing.data ? decimalToNumber(pricing.data.fees.gross) : 0,
        schedulingMethods: selectedProduct?.schedulingMethods,
    });

    const { targets: collectionTargets, numInstalments: derivedNumInstalments } = itemBased;

    const {
        firstTargetMinor,
        configuredMaxDepositMinor,
        effectiveMaxExclusive,
        configuredMaxBinds,
        naturalCeilingLabel,
        depositExceedsEffectiveMax,
        previewDepositMinor,
    } = useDepositCeiling({
        isReadOnly,
        isDepositBearing,
        isItemBasedDepositBearing,
        isItemBasedFamily,
        collectionTargets,
        isBasketDerived: basketGross.kind === 'derived',
        isPricingUpdating: pricing.isUpdating,
        basketGrossMinor,
        maxDepositAmountMinor: selectedProduct?.maxDepositAmountMinor,
        effectiveDepositMinor,
        depositMinor,
        depositOrigin,
        isDepositFixed: depositMode.kind === 'fixed',
        discardProductDeposit,
    });

    // Stale/past-dated quote: the earliest priced service has already fallen due, so no valid
    // schedule exists. Gates the step, withholds the preview and hides the deposit column.
    const pastDueForScheduling = isPastDueForScheduling({
        earliestDue: earliestPricedDueDate(merged.services, merged.addOns),
        anchor,
    });

    // `null` unless the product opts in, the method is even-monthly and a term yields a non-empty
    // range. The transmitted count can never exceed term - lead, chosen or preselected.
    const collectionChoice = collectionCountChoice({
        allowChoice: isEvenMonthlyFamily && selectedProduct?.allowCollectionInstalmentChoice === true,
        termMonths: evenMonthly.kind === 'offerable' ? evenMonthly.termMonths : undefined,
        leadMonths: effectiveLeadMonths,
        derivedCount: evenMonthly.kind === 'offerable' ? evenMonthly.numInstalments : 0,
        minInstalments: selectedProduct?.minCollectionInstalments,
        maxInstalments: selectedProduct?.maxCollectionInstalments,
        // A deposit (explicit > 0 or the default/equal-split one) spends one instalment, so a
        // single-collection schedule is invalid — elemis rejects it. An explicit £0 does not.
        depositConsumesInstalment: isDepositBearing && previewDepositMinor !== 0,
    });
    const chosenInstalments = resolveChosenInstalments(collectionChoice, collectionInstalments);
    const transmittedInstalmentCount = transmittedCollectionCount(collectionChoice, collectionInstalments);

    const preview = useSchedulePreviewParams({
        isReadOnly,
        canPreviewSchedule,
        selectedMethod,
        basketGrossMinor,
        isDepositBearing,
        depositMinor: previewDepositMinor,
        isItemBasedFamily,
        collectionTargets,
        derivedNumInstalments,
        depositExceedsEffectiveMax,
        leadOutOfBounds,
        pastDueForScheduling,
        isEvenMonthlyFamily,
        evenMonthly,
        evenMonthlyInstalmentsOverride: transmittedInstalmentCount,
        anchor,
    });
    const { params, data, isLoading, isFetching, error, isPlaceholderData, appliedDeposit, scheduleSetDepositMinor } =
        preview;

    const { isDepositWriteBackPending } = useScheduleDepositWriteBack({
        isReadOnly,
        isPricingUpdating: pricing.isUpdating,
        depositOrigin,
        preview,
        minDepositMinor: selectedProduct?.minDepositAmountMinor,
        effectiveMaxExclusive,
        writeScheduleDeposit,
    });

    // The exclusive ceiling the pane asks the user to come under; undefined
    // when there is no ceiling rejection to correct.
    const depositCeilingRejection =
        basketGross.kind === 'derived' && depositExceedsEffectiveMax ? effectiveMaxExclusive : undefined;

    const { isCurrentSelectionConfirmed, showStaleNotice } = resolveSelectionConfirmation({
        paymentSelection,
        selectedMethodKey,
        params,
        data,
        recordedLeadMonths,
    });

    const termWarning = !!params && isEvenMonthlyFamily ? termWarningCopy(evenMonthly) : null;
    const leadRejectionMessage = resolveLeadRejectionMessage(leadRejection, leadOverrideMonths, leadBounds);

    const { isLiveScheduleRecorded } = usePaymentSelectionCommit({
        isReadOnly,
        selectedMethod,
        params,
        data,
        error,
        isFetching,
        isPlaceholderData,
        anchor,
        recordedLeadMonths,
        paymentSelection,
        isCurrentSelectionConfirmed,
        updatePaymentSelection,
        // The who-collects choice belongs to the summary's modal gate, so this step commits no
        // routing and its preview always shows the full schedule.
        collectionRouting: undefined,
        isDealerConfigUnresolved: false,
        isDealerReceiptIncomplete: false,
        methods,
        selectedMethodKey,
        unavailableReasons,
        hasDepositCeilingRejection: depositCeilingRejection !== undefined,
        hasLeadRejection: leadRejectionMessage !== null,
        hasPastServiceDate: pastDueForScheduling,
        isDepositWriteBackPending,
        reportPaymentPreviewBlocked,
    });

    if (isReadOnly) {
        return (
            <ReadOnlyPaymentView
                methods={methods}
                paymentSelection={paymentSelection}
                currency={currency}
            />
        );
    }

    const { showDepositSection, showCollectionCount, reserveControlsColumn } = resolveControlsVisibility({
        isBasketDerived: basketGross.kind === 'derived',
        pastDueForScheduling,
        isDepositBearing,
        isItemBasedDepositBearing,
        firstTargetMinor,
        hasCollectionChoice: collectionChoice !== null,
        productOffersControls:
            selectedProduct?.allowDeposit === true ||
            selectedProduct?.allowCollectionInstalmentChoice === true,
    });

    const schedulePane = (
        <SchedulePreviewPane
            selectedMethod={selectedMethod}
            params={params}
            data={data}
            isLoading={isLoading}
            isFetching={isFetching}
            error={error}
            currency={currency}
            appliedDeposit={appliedDeposit}
            isPlaceholderData={isPlaceholderData}
            showStaleNotice={showStaleNotice}
            isDisplayedScheduleRecorded={isLiveScheduleRecorded}
            anchor={anchor}
        />
    );

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap justify-between content-start items-start">
                <PageHeader
                    title="Payment Options"
                    subtitle="Choose how the customer will spread payments and preview the schedule."
                />
            </div>

            {!selectedProduct ? (
                <Alert variant="warning" role="note">
                    Select a product before choosing a payment schedule.
                </Alert>
            ) : (
                <>
                    <SchedulingMethodSelector
                        methods={methods}
                        selectedKey={selectedMethodKey}
                        onChange={(method) => setSelectedMethodKey(method.key)}
                        unavailableReasons={unavailableReasons}
                    />

                    <TermWarningNotice termWarning={termWarning} />

                    {leadBounds !== null && isItemBasedFamily && (
                        <CollectionLeadInput
                            min={leadBounds.min}
                            max={leadBounds.max}
                            productDefault={selectedProduct.collectionLeadMonths ?? 0}
                            valueMonths={effectiveLeadMonths}
                            onDebouncedChange={setLeadOverrideMonths}
                        />
                    )}

                    <LeadRejectionNotice message={leadRejectionMessage} />

                    <PastServiceDateNotice show={pastDueForScheduling} />

                    <UnpricedNotice
                        itemNames={basketGross.kind === 'unpriced' ? basketGross.itemNames : null}
                    />
                    <PricingPendingNotice pending={basketGross.kind === 'pending'} />
                    <PricingErrorNotice hasError={basketGross.kind === 'error'} onRetry={pricing.refetch} />

                    {reserveControlsColumn ? (
                        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(16rem,20rem)] lg:items-start">
                            {schedulePane}
                            <div className="mt-4 space-y-4">
                                {showCollectionCount && collectionChoice !== null && (
                                    <CollectionCountSelect
                                        min={collectionChoice.min}
                                        max={collectionChoice.max}
                                        value={chosenInstalments ?? collectionChoice.preselect}
                                        onChange={setCollectionInstalments}
                                    />
                                )}
                                {showDepositSection && (
                                    <DepositSection
                                        selectedProduct={selectedProduct}
                                        effectiveMaxExclusive={effectiveMaxExclusive}
                                        configuredMaxBinds={configuredMaxBinds}
                                        configuredMaxDepositMinor={configuredMaxDepositMinor}
                                        naturalCeilingLabel={naturalCeilingLabel}
                                        mode={depositMode}
                                        currency={currency}
                                        depositMinor={depositMinor}
                                        depositDiscards={depositDiscards}
                                        depositOrigin={depositOrigin}
                                        onDepositInput={onDepositInput}
                                        onDepositEdit={onDepositEdit}
                                        scheduleSetDepositMinor={scheduleSetDepositMinor}
                                        isDepositWriteBackPending={isDepositWriteBackPending}
                                        ceilingRejectionMinor={depositCeilingRejection}
                                    />
                                )}
                            </div>
                        </div>
                    ) : (
                        schedulePane
                    )}
                </>
            )}
        </div>
    );
};
